import React, { useMemo, useState, useEffect } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";
import FolderIcon from "@mui/icons-material/Folder";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import CloseIcon from "@mui/icons-material/Close";
import PersonIcon from "@mui/icons-material/Person";
import { formatDate } from "../utils";
import {PORTAL_GRADIENT} from "./AdminStyles";

const getLogDate = (r) => r.created_at || "";
const getLogBucket = (r) => r.bucket_name || "";
const getLogFile = (r) => r.key || "";

export default function AuditLogDialog({
  open,
  onClose,
  user,
  logs,
  loading,
  error,
  filters,
  setFilters,
}) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const filteredLogs = useMemo(() => {
    const d = filters.date.toLowerCase();
    const b = filters.bucket.toLowerCase();
    const f = filters.file.toLowerCase();
    return (logs || []).filter((r) => {
      const dv = String(getLogDate(r) || "").toLowerCase();
      const bv = String(getLogBucket(r) || "").toLowerCase();
      const fv = String(getLogFile(r) || "").toLowerCase();
      return (
        (!d || dv.includes(d)) &&
        (!b || bv.includes(b)) &&
        (!f || fv.includes(f))
      );
    });
  }, [logs, filters]);

  // Reset to first page when filters/logs change
  useEffect(() => {
    setPage(0);
  }, [filters, logs]);

  const paginatedLogs = useMemo(
    () => filteredLogs.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredLogs, page, rowsPerPage]
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
          boxShadow: "0 20px 60px rgba(63, 81, 181, 0.25)",
          width: 1200,
          maxWidth: "95vw",
          height: 720,
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          p: 0,
          background: PORTAL_GRADIENT,
          color: "white",
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 2, py: 1.25 }}
        >
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: "50%",
                background: "rgba(255, 215, 0, 0.25)",
                border: "2px solid rgba(255, 215, 0, 0.6)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <HistoryIcon sx={{ color: "#ffd700", fontSize: 18 }} />
            </Box>
            <Box>
              <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.1 }}>
                Download History
              </Typography>
              {user && (
                <Stack direction="row" spacing={1} alignItems="center">
                  <PersonIcon sx={{ fontSize: 12, color: "rgba(255,255,255,0.85)" }} />
                  <Typography
                    variant="caption"
                    sx={{ color: "rgba(255,255,255,0.9)", lineHeight: 1.2 }}
                  >
                    {user.display_name || user.email}
                  </Typography>
                </Stack>
              )}
            </Box>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              label={`${filteredLogs.length} ${filteredLogs.length === 1 ? "entry" : "entries"}`}
              size="small"
              sx={{
                height: 22,
                backgroundColor: "rgba(255,255,255,0.2)",
                color: "white",
                fontWeight: 600,
                border: "1px solid rgba(255,255,255,0.3)",
              }}
            />
            <Tooltip title="Close">
              <IconButton
                onClick={onClose}
                size="small"
                sx={{
                  background: "linear-gradient(135deg, #ff5252 0%, #d32f2f 100%)",
                  color: "white",
                  width: 28,
                  height: 28,
                  boxShadow: "0 2px 8px rgba(211, 47, 47, 0.4)",
                  transition: "all 0.2s",
                  "&:hover": {
                    background: "linear-gradient(135deg, #ff1744 0%, #b71c1c 100%)",
                    transform: "rotate(90deg) scale(1.05)",
                  },
                }}
              >
                <CloseIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          background: "linear-gradient(180deg, #f8f9ff 0%, #ffffff 100%)",
          p: 2.5,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            {error}
          </Alert>
        )}
        <Paper
          elevation={0}
          sx={{
            border: "1px solid #e0e0e0",
            borderRadius: 2,
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            minHeight: 0,
          }}
        >
          <TableContainer sx={{ flex: 1, overflow: "auto", minHeight: 0 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow
                  sx={{ background: "linear-gradient(90deg, #e3f2fd 0%, #e8eaf6 50%, #f3e5f5 100%)" }}
                >
                  <TableCell sx={{ fontWeight: 700, width: "25%", color: "#1a237e", backgroundColor: "#e3f2fd" }}>📅 Date</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: "30%", color: "#2e7d32", backgroundColor: "#e8eaf6" }}>🗂️ Bucket Name</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: "#6a1b9a", backgroundColor: "#f3e5f5" }}>📄 File</TableCell>
                </TableRow>
                <TableRow sx={{ backgroundColor: "#fafbff" }}>
                  <TableCell sx={{ backgroundColor: "#fafbff", top: 37 }}>
                    <TextField
                      size="small"
                      fullWidth
                      placeholder="Filter date"
                      value={filters.date}
                      onChange={(e) => setFilters((f) => ({ ...f, date: e.target.value }))}
                      sx={{ "& .MuiOutlinedInput-root": { backgroundColor: "white" } }}
                    />
                  </TableCell>
                  <TableCell sx={{ backgroundColor: "#fafbff", top: 37 }}>
                    <TextField
                      size="small"
                      fullWidth
                      placeholder="Filter bucket"
                      value={filters.bucket}
                      onChange={(e) => setFilters((f) => ({ ...f, bucket: e.target.value }))}
                      sx={{ "& .MuiOutlinedInput-root": { backgroundColor: "white" } }}
                    />
                  </TableCell>
                  <TableCell sx={{ backgroundColor: "#fafbff", top: 37 }}>
                    <TextField
                      size="small"
                      fullWidth
                      placeholder="Filter file"
                      value={filters.file}
                      onChange={(e) => setFilters((f) => ({ ...f, file: e.target.value }))}
                      sx={{ "& .MuiOutlinedInput-root": { backgroundColor: "white" } }}
                    />
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  [1, 2, 3, 4].map((i) => (
                    <TableRow key={i}>
                      <TableCell><Skeleton variant="text" /></TableCell>
                      <TableCell><Skeleton variant="text" /></TableCell>
                      <TableCell><Skeleton variant="text" /></TableCell>
                    </TableRow>
                  ))
                ) : filteredLogs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} sx={{ py: 6, textAlign: "center", height: 300 }}>
                      <HistoryIcon sx={{ fontSize: 48, color: "#bdbdbd", mb: 1 }} />
                      <Typography variant="body2" color="text.secondary">
                        No download log entries found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedLogs.map((r, idx) => (
                    <TableRow
                      key={r.id ?? `${page}-${idx}`}
                      sx={{
                        "&:hover": { backgroundColor: "#f0f3ff" },
                        "&:nth-of-type(even)": { backgroundColor: "#fcfcff" },
                      }}
                    >
                      <TableCell>
                        <Typography variant="body2" sx={{ color: "#1a237e", fontWeight: 500 }}>
                          {formatDate(getLogDate(r)) || "-"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={<FolderIcon sx={{ fontSize: 16 }} />}
                          label={getLogBucket(r) || "-"}
                          size="small"
                          sx={{ backgroundColor: "#e8f5e9", color: "#2e7d32", fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <InsertDriveFileIcon sx={{ fontSize: 16, color: "#6a1b9a" }} />
                          <Typography variant="body2" sx={{ wordBreak: "break-all", color: "#4a148c" }}>
                            {getLogFile(r) || "-"}
                          </Typography>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {!loading && filteredLogs.length > 0 && (
            <TablePagination
              component="div"
              count={filteredLogs.length}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25, 50]}
              sx={{
                flexShrink: 0,
                borderTop: "1px solid #e0e0e0",
                backgroundColor: "#fafbff",
                "& .MuiTablePagination-toolbar": {
                  minHeight: 48,
                },
                "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                  margin: 0,
                  alignSelf: "center",
                },
                "& .MuiTablePagination-actions": {
                  marginLeft: 1,
                },
              }}
            />
          )}
        </Paper>
      </DialogContent>

      <DialogActions
        sx={{
          background: "linear-gradient(90deg, #f5f7ff 0%, #eef1ff 100%)",
          px: 3,
          py: 1.5,
          borderTop: "1px solid #e0e0e0",
        }}
      >
        <Button
          onClick={onClose}
          variant="contained"
          startIcon={<CloseIcon />}
          sx={{
            background: "linear-gradient(135deg, #ff5252 0%, #d32f2f 100%)",
            color: "white",
            textTransform: "none",
            fontWeight: 600,
            borderRadius: 2,
            px: 3,
            boxShadow: "0 2px 8px rgba(211, 47, 47, 0.35)",
            "&:hover": {
              background: "linear-gradient(135deg, #ff1744 0%, #b71c1c 100%)",
              boxShadow: "0 4px 12px rgba(211, 47, 47, 0.5)",
            },
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}